/**
 * Relationship Editor Component
 *
 * Form for creating relationships between graph nodes
 */
import React, { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { knowledgeGraphStore } from '@/stores/KnowledgeGraphStore'
import { RelationType, GraphRelationshipCreate } from '@/types'
import toast from 'react-hot-toast'

interface RelationshipEditorProps {
  graphId: string
  sourceNodeId?: string // Pre-selected source node
  targetNodeId?: string // Pre-selected target node
  onClose: () => void
  onSave?: () => void
}

const RelationshipEditor: React.FC<RelationshipEditorProps> = observer(({
  graphId,
  sourceNodeId: initialSourceNodeId,
  targetNodeId: initialTargetNodeId,
  onClose,
  onSave,
}) => {
  const { nodes } = knowledgeGraphStore
  const [sourceNodeId, setSourceNodeId] = useState(initialSourceNodeId || '')
  const [targetNodeId, setTargetNodeId] = useState(initialTargetNodeId || '')
  const [relationType, setRelationType] = useState<RelationType>(RelationType.RELATED_TO)
  const [label, setLabel] = useState('')
  const [weight, setWeight] = useState<number>(1.0)
  const [properties, setProperties] = useState<Record<string, any>>({})
  const [propertyKey, setPropertyKey] = useState('')
  const [propertyValue, setPropertyValue] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    // Load nodes if not already loaded
    if (nodes.length === 0) {
      knowledgeGraphStore.fetchNodes(graphId)
    }
  }, [graphId, nodes.length])

  const handleAddProperty = () => {
    if (propertyKey.trim()) {
      setProperties({ ...properties, [propertyKey]: propertyValue })
      setPropertyKey('')
      setPropertyValue('')
    }
  }

  const handleRemoveProperty = (key: string) => {
    const newProps = { ...properties }
    delete newProps[key]
    setProperties(newProps)
  }

  const handleSave = async () => {
    if (!sourceNodeId || !targetNodeId) {
      toast.error('Please select both source and target nodes')
      return
    }

    if (sourceNodeId === targetNodeId) {
      toast.error('Source and target nodes must be different')
      return
    }

    setIsSaving(true)

    try {
      const createData: GraphRelationshipCreate = {
        source_node_id: sourceNodeId,
        target_node_id: targetNodeId,
        relation_type: relationType,
        label: label.trim() || undefined,
        properties,
        weight,
      }

      await knowledgeGraphStore.createRelationship(graphId, createData)
      toast.success('Relationship created successfully')
      if (onSave) {
        onSave()
      }
      onClose()
    } catch (error: any) {
      toast.error(error.message || 'Failed to create relationship')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
          <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Create Relationship
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Source Node */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Source Node <span className="text-red-500">*</span>
                </label>
                <select
                  value={sourceNodeId}
                  onChange={(e) => setSourceNodeId(e.target.value)}
                  disabled={!!initialSourceNodeId}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-ring sm:text-sm disabled:bg-gray-100"
                >
                  <option value="">Select source node</option>
                  {nodes.map((node) => (
                    <option key={node.id} value={node.id}>
                      {node.label} ({node.node_type})
                    </option>
                  ))}
                </select>
              </div>

              {/* Relationship Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Relationship Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={relationType}
                  onChange={(e) => setRelationType(e.target.value as RelationType)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-ring sm:text-sm"
                >
                  {Object.values(RelationType).map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              {/* Target Node */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Target Node <span className="text-red-500">*</span>
                </label>
                <select
                  value={targetNodeId}
                  onChange={(e) => setTargetNodeId(e.target.value)}
                  disabled={!!initialTargetNodeId}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-ring sm:text-sm disabled:bg-gray-100"
                >
                  <option value="">Select target node</option>
                  {nodes.map((node) => (
                    <option key={node.id} value={node.id}>
                      {node.label} ({node.node_type})
                    </option>
                  ))}
                </select>
              </div>

              {/* Label (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Label (optional)
                </label>
                <input
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Custom label for this relationship"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-ring sm:text-sm"
                />
              </div>

              {/* Weight */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Weight (0.0 - 1.0)
                </label>
                <input
                  type="number"
                  min="0"
                  max="1"
                  step="0.1"
                  value={weight}
                  onChange={(e) => setWeight(parseFloat(e.target.value))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-ring sm:text-sm"
                />
              </div>

              {/* Properties */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Properties
                </label>
                <div className="space-y-2">
                  {Object.entries(properties).map(([key, value]) => (
                    <div key={key} className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                      <span className="text-sm font-medium text-gray-700 flex-shrink-0">{key}:</span>
                      <span className="text-sm text-gray-600 flex-1">{String(value)}</span>
                      <button
                        onClick={() => handleRemoveProperty(key)}
                        className="text-red-600 hover:text-red-600 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>

                <div className="mt-2 flex gap-2">
                  <input
                    type="text"
                    value={propertyKey}
                    onChange={(e) => setPropertyKey(e.target.value)}
                    placeholder="Key"
                    className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-ring sm:text-sm"
                  />
                  <input
                    type="text"
                    value={propertyValue}
                    onChange={(e) => setPropertyValue(e.target.value)}
                    placeholder="Value"
                    className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-ring sm:text-sm"
                  />
                  <button
                    onClick={handleAddProperty}
                    disabled={!propertyKey.trim()}
                    className="px-3 py-2 bg-primary text-white text-sm font-medium rounded-md hover:bg-primary/90 disabled:bg-gray-300"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving || !sourceNodeId || !targetNodeId}
              className="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 sm:ml-3 sm:w-auto disabled:bg-gray-300"
            >
              {isSaving ? 'Creating...' : 'Create'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  )
})

RelationshipEditor.displayName = 'RelationshipEditor'

export default RelationshipEditor
